import { useState } from 'react';
import type { Person, PendingInvitation, InvitableRole } from '../api.js';
import type { Translate } from '../i18n/index.js';
import { accent, ink, theme } from '../theme.js';
import { Card, PageTitle } from '../components/ui.js';
import { looksStrongEnough, suggestPassword } from '../password.js';

/**
 * Who can reach this company's books.
 *
 * Access used to be granted by writing SQL, which meant every new person at a
 * client was a job for whoever held the database password, and meant nobody
 * could answer "who can see our books?" without being shown a query result.
 *
 * Both halves are here together on purpose: someone already using the service,
 * and someone who has been offered it and not yet signed in, are the same
 * question from a client's point of view — who has access to our accounts.
 */

const ROLE_LABELS: Record<InvitableRole, string> = {
  cfoai_admin: 'CFO · Admin',
  cfoai_staff: 'CFO · Staff',
  client_admin: 'Organisation admin',
  client_user: 'Data entry',
};

const ROLE_EXPLANATIONS: Record<InvitableRole, string> = {
  cfoai_admin: 'Everything, including managing who has access',
  cfoai_staff: 'Reviews and files documents into Xero',
  client_admin: 'Files documents into Xero, and manages people and settings',
  client_user: 'Creates documents and files them into Xero',
};

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toISOString().slice(0, 10);
}

export function PeopleScreen({
  organisation,
  members,
  invited,
  grantableRoles,
  createsSignIn,
  canRemoveFirmMembers,
  busy,
  error,
  t,
  onInvite,
  onRevoke,
  onCreateSignIn,
  onSetPassword,
}: {
  organisation: string;
  members: Person[];
  invited: PendingInvitation[];
  grantableRoles: InvitableRole[];
  createsSignIn: boolean;
  canRemoveFirmMembers: boolean;
  busy: boolean;
  error: string | null;
  t: Translate;
  onInvite: (
    email: string,
    role: InvitableRole,
    name: string | null,
    password: string | null,
  ) => Promise<boolean>;
  onRevoke: (target: { userId?: number; invitationId?: number }) => void;
  onCreateSignIn: (invitationId: number) => void;
  onSetPassword: (
    target: { userId?: number; invitationId?: number },
    password: string,
  ) => Promise<boolean>;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<InvitableRole>(grantableRoles[0] ?? 'client_user');
  const [copied, setCopied] = useState(false);

  /*
    The password being chosen for a new person, and whether it is on screen.

    Hidden by default, because this screen gets shown to the room during an
    onboarding call more often than anybody plans for, and shown on request,
    because an admin about to read a password aloud needs to see it.
  */
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  /*
    A password that has just been set, held only until this screen is left.

    This is the only copy. It is not in the database and it is not in the
    directory in any readable form, so losing it here means setting another
    one — which is the trade this design makes on purpose, and the panel says
    so rather than letting somebody discover it.
  */
  const [issued, setIssued] = useState<{ who: string; password: string } | null>(null);

  /** Who is having their password changed, once somebody has started. */
  const [changing, setChanging] = useState<{
    who: string;
    target: { userId?: number; invitationId?: number };
    password: string;
  } | null>(null);

  /*
    Where somebody signs in, read from the page rather than written down.
    A hard-coded address is one more thing that silently stops being true the
    day the site moves to its own domain.
  */
  const signInUrl = window.location.origin;

  const inputStyle = {
    height: '36px',
    border: `1px solid ${theme.borderStrong}`,
    borderRadius: theme.radiusControl,
    padding: '0 10px',
    fontSize: '13px',
    background: '#fff',
    minWidth: 0,
  };

  return (
    <div style={{ padding: '22px 24px 40px', maxWidth: '1000px' }}>
      <PageTitle
        title={t('People')}
        sub={`${t('Who can reach the books of')} ${organisation}. ${t(
          'Access can be withdrawn at any time.',
        )}`}
      />
      <div style={{ height: '18px' }} />

      <Card style={{ marginBottom: '16px' }}>
        <div
          style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${theme.dividerSoft}`,
            fontWeight: 600,
            fontSize: '13px',
            background: theme.surfaceHeader,
          }}
        >
          {t('Give someone access')}
        </div>
        <div style={{ padding: '14px 16px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' }}>
            <label style={{ flex: '1 1 260px', minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '5px' }}>
                {t('Email address')}
              </span>
              <input
                type="email"
                dir="ltr"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.qa"
                style={{ ...inputStyle, width: '100%' }}
              />
            </label>

            {/*
              Optional, and asked for because the person inviting usually
              knows the answer. Without it the directory shows whatever can be
              guessed from the address, and "Info" or "Accounts" is what a
              colleague then sees next to their own name.
            */}
            <label style={{ flex: '1 1 200px', minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '5px' }}>
                {t('Their name')} <span style={{ fontWeight: 400, color: theme.textMuted }}>({t('optional')})</span>
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{ ...inputStyle, width: '100%' }}
              />
            </label>

            <label style={{ flex: '0 1 220px', minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '5px' }}>
                {t('Role')}
              </span>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as InvitableRole)}
                style={{ ...inputStyle, width: '100%' }}
              >
                {grantableRoles.map((r) => (
                  <option key={r} value={r}>
                    {t(ROLE_LABELS[r])}
                  </option>
                ))}
              </select>
            </label>

            <button
              type="button"
              disabled={busy || email.trim() === '' || (password !== '' && !looksStrongEnough(password))}
              onClick={() => {
                const chosen = password === '' ? null : password;
                const who = name.trim() || email.trim();
                void onInvite(email.trim(), role, name.trim() || null, chosen).then((ok) => {
                  if (!ok) return;
                  // Only cleared on success, so a refused address is still in
                  // the box to be corrected rather than typed again.
                  setEmail('');
                  setName('');
                  setPassword('');
                  setShowPassword(false);
                  if (chosen) setIssued({ who, password: chosen });
                });
              }}
              style={{
                height: '36px',
                background:
                  busy || email.trim() === '' || (password !== '' && !looksStrongEnough(password))
                    ? theme.borderStrong
                    : theme.nav,
                color: '#fff',
                border: 0,
                borderRadius: theme.radiusControl,
                padding: '0 18px',
                fontSize: '13px',
                fontWeight: 600,
                cursor:
                  busy || email.trim() === '' || (password !== '' && !looksStrongEnough(password))
                    ? 'default'
                    : 'pointer',
              }}
            >
              {t('Give access')}
            </button>
          </div>

          {/*
            Optional, and the empty case is the one that was there before:
            no password, a throwaway in the directory, and the person sets
            their own from the sign-in page. Filling it in is for the other
            situation — an accountant on the phone to a client who needs to
            be in their books before the call ends.
          */}
          <div style={{ marginTop: '12px' }}>
            <PasswordField
              id="invite-password"
              label={`${t('Set their password')} (${t('optional')})`}
              value={password}
              shown={showPassword}
              onValue={setPassword}
              onShow={setShowPassword}
              t={t}
            />
            <div style={{ marginTop: '6px', fontSize: '12px', color: theme.textMuted, lineHeight: 1.6 }}>
              {t(
                password === ''
                  ? 'Leave this empty and they set their own password from the sign-in page. The code Microsoft emails them can take a few minutes to arrive.'
                  : 'You will see this password once, after access is given. It is not stored anywhere.',
              )}
            </div>
          </div>

          <div style={{ marginTop: '9px', fontSize: '12px', color: theme.textMuted, lineHeight: 1.6 }}>
            {t(ROLE_EXPLANATIONS[role])}.{' '}
            {t(
              createsSignIn
                ? 'An account is created for them straight away. The access is waiting the first time they sign in.'
                : 'They do not need an account yet. The next time this address signs in, the access is already waiting.',
            )}
          </div>

          {/*
            Said plainly, because the screen used to say "Invited" and leave
            somebody waiting for a message that was never going to arrive.
          */}
          <div
            style={{
              marginTop: '12px',
              padding: '11px 13px',
              background: theme.surfaceMuted,
              border: `1px solid ${theme.dividerSoft}`,
              borderRadius: theme.radiusCard,
              fontSize: '12px',
              color: theme.text,
              lineHeight: 1.7,
            }}
          >
            <strong>{t(createsSignIn ? 'No invitation is emailed.' : 'Nothing is emailed.')}</strong>{' '}
            {t(
              createsSignIn
                ? 'Send them this address yourself. If you set a password above, that is all they need. If you did not, they choose "Forgot password?" on the sign-in page and collect the code Microsoft emails them — which can take a few minutes and is worth checking the junk folder for.'
                : 'Send them this address yourself and tell them to sign up with the same email you entered above.',
            )}
            <div style={{ marginTop: '7px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <code dir="ltr" style={{ fontFamily: theme.fontMono, fontSize: '11.5px', wordBreak: 'break-all' }}>
                {signInUrl}
              </code>
              <button
                type="button"
                onClick={() => void navigator.clipboard?.writeText(signInUrl).then(
                  () => setCopied(true),
                  () => setCopied(false),
                )}
                style={{
                  background: 'transparent',
                  border: `1px solid ${theme.borderStrong}`,
                  borderRadius: theme.radiusControl,
                  padding: '5px 11px',
                  fontSize: '11.5px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  color: theme.text,
                }}
              >
                {copied ? t('Copied') : t('Copy')}
              </button>
            </div>
          </div>

          {issued && (
            <IssuedPassword
              who={issued.who}
              password={issued.password}
              onDone={() => setIssued(null)}
              t={t}
            />
          )}

          {error && (
            <div style={{ marginTop: '10px', fontSize: '12.5px', color: theme.badDeep, fontWeight: 600 }}>
              {t(error)}
            </div>
          )}
        </div>
      </Card>

      {changing && (
        <Card style={{ marginBottom: '16px', borderColor: accent }}>
          <div
            style={{
              padding: '12px 16px',
              borderBottom: `1px solid ${theme.dividerSoft}`,
              fontWeight: 600,
              fontSize: '13px',
              background: theme.surfaceHeader,
            }}
          >
            {t('New password for')} {changing.who}
          </div>
          <div style={{ padding: '14px 16px' }}>
            <PasswordField
              id="change-password"
              label={t('New password')}
              value={changing.password}
              shown
              onValue={(next) => setChanging({ ...changing, password: next })}
              onShow={() => undefined}
              t={t}
            />

            <div style={{ marginTop: '9px', fontSize: '12px', color: theme.textMuted, lineHeight: 1.6 }}>
              {t(
                'Their old password stops working straight away. Nothing is emailed to them, so tell them what it is.',
              )}
            </div>

            <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                type="button"
                disabled={busy || !looksStrongEnough(changing.password)}
                onClick={() => {
                  const { who, target, password: next } = changing;
                  void onSetPassword(target, next).then((ok) => {
                    if (!ok) return;
                    setChanging(null);
                    setIssued({ who, password: next });
                  });
                }}
                style={{
                  height: '36px',
                  background:
                    busy || !looksStrongEnough(changing.password) ? theme.borderStrong : theme.nav,
                  color: '#fff',
                  border: 0,
                  borderRadius: theme.radiusControl,
                  padding: '0 18px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: busy || !looksStrongEnough(changing.password) ? 'default' : 'pointer',
                }}
              >
                {t('Change the password')}
              </button>
              <button
                type="button"
                onClick={() => setChanging(null)}
                style={{
                  height: '36px',
                  background: 'transparent',
                  border: `1px solid ${theme.borderStrong}`,
                  borderRadius: theme.radiusControl,
                  padding: '0 16px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: theme.text,
                  cursor: 'pointer',
                }}
              >
                {t('Cancel')}
              </button>
            </div>

            {error && (
              <div
                style={{ marginTop: '10px', fontSize: '12.5px', color: theme.badDeep, fontWeight: 600 }}
              >
                {t(error)}
              </div>
            )}
          </div>
        </Card>
      )}

      <Card>
        <div
          style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${theme.dividerSoft}`,
            fontWeight: 600,
            fontSize: '13px',
            background: theme.surfaceHeader,
          }}
        >
          {t('Has access')} · {members.length + invited.length}
        </div>

        {members.length === 0 && invited.length === 0 && (
          <div style={{ padding: '18px 16px', color: theme.textMuted, fontSize: '13px' }}>
            {t('Nobody has access to this organisation yet.')}
          </div>
        )}

        {members.map((person) => {
          /*
            An organisation's own admin manages their colleagues, not their
            accountants. The server refuses either way; this keeps the screen
            from offering something it knows will fail.
          */
          const isFirm = person.role === 'cfoai_staff' || person.role === 'cfoai_admin';
          const removable = canRemoveFirmMembers || !isFirm;
          return (
            <Row
              key={`m${person.userId}`}
              primary={person.displayName ?? person.email ?? t('Unnamed')}
              secondary={person.displayName ? person.email : null}
              role={t(ROLE_LABELS[person.role])}
              note={
                removable
                  ? `${t('Since')} ${formatDate(person.since)}`
                  : `${t('Since')} ${formatDate(person.since)} · ${t('Your accountant')}`
              }
              actionLabel={removable ? t('Remove') : null}
              busy={busy}
              onAction={() => onRevoke({ userId: person.userId })}
              /*
                `removable` is the same question: is this one of our own
                people, and are you one of us. A client admin may do neither
                to an accountant, and the server refuses both.
              */
              extraLabel={person.canSetPassword && removable ? t('Set password') : null}
              onExtra={() =>
                setChanging({
                  who: person.displayName ?? person.email ?? t('Unnamed'),
                  target: { userId: person.userId },
                  password: suggestPassword(),
                })
              }
            />
          );
        })}

        {invited.map((invitation) => {
          const signIn = signInStatus(invitation, createsSignIn, t);
          const isFirm =
            invitation.role === 'cfoai_staff' || invitation.role === 'cfoai_admin';
          const mayManage = canRemoveFirmMembers || !isFirm;
          return (
            <Row
              key={`i${invitation.invitationId}`}
              primary={invitation.displayName ?? invitation.email}
              secondary={invitation.displayName ? invitation.email : null}
              role={t(ROLE_LABELS[invitation.role])}
              note={signIn.note}
              noteDetail={signIn.detail}
              pending
              actionLabel={t('Withdraw')}
              busy={busy}
              onAction={() => onRevoke({ invitationId: invitation.invitationId })}
              /*
                One extra action per row, and which one depends on what is
                wrong. A sign-in that was never made needs making before its
                password means anything, so that offer comes first; once it
                exists, the useful offer is a new password.
              */
              extraLabel={
                signIn.canRetry ? t('Create sign-in')
                : invitation.canSetPassword && mayManage ? t('Set password')
                : null
              }
              onExtra={() => {
                if (signIn.canRetry) return onCreateSignIn(invitation.invitationId);
                setChanging({
                  who: invitation.displayName ?? invitation.email,
                  target: { invitationId: invitation.invitationId },
                  password: suggestPassword(),
                });
              }}
            />
          );
        })}
      </Card>
    </div>
  );
}

/**
 * What to say about an invited person's sign-in, and whether to offer another go.
 *
 * The distinction that matters is between "there is no account" and "nobody
 * asked". A null state means the second: either the invitation predates
 * account creation, or this deployment never had directory credentials. Saying
 * "no account" there would send an admin chasing a problem that may not exist.
 */
function signInStatus(
  invitation: PendingInvitation,
  createsSignIn: boolean,
  t: Translate,
): { note: string; detail: string | null; canRetry: boolean } {
  switch (invitation.accountState) {
    case 'created':
    case 'already_existed':
      return { note: t('Sign-in ready — waiting for them'), detail: null, canRetry: false };

    case 'refused':
      // Worth showing what the directory said: almost always something an
      // admin can act on, like an address the directory will not accept.
      return { note: t('Sign-in refused'), detail: invitation.accountDetail, canRetry: true };

    case 'unavailable':
      return { note: t('Sign-in not created yet'), detail: invitation.accountDetail, canRetry: true };

    default:
      return createsSignIn
        ? { note: t('No sign-in was made for this invitation'), detail: null, canRetry: true }
        : {
            note: t('Waiting — no email is sent; they must sign in themselves'),
            detail: null,
            canRetry: false,
          };
  }
}

function Row({
  primary,
  secondary,
  role,
  note,
  noteDetail,
  pending,
  actionLabel,
  busy,
  onAction,
  extraLabel,
  onExtra,
}: {
  primary: string;
  secondary: string | null;
  role: string;
  note: string;
  noteDetail?: string | null;
  pending?: boolean;
  actionLabel: string | null;
  busy: boolean;
  onAction: () => void;
  extraLabel?: string | null;
  onExtra?: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        borderBottom: `1px solid ${theme.divider}`,
        background: pending ? theme.surfaceMuted : 'transparent',
      }}
    >
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: 600 }} dir={secondary ? undefined : 'ltr'}>
          {primary}
        </div>
        {secondary && (
          <div style={{ fontSize: '12px', color: theme.textMuted }} dir="ltr">
            {secondary}
          </div>
        )}
      </div>

      <div style={{ flex: '0 1 180px', fontSize: '12.5px' }}>{role}</div>

      <div style={{ flex: '0 1 200px', fontSize: '12px', color: theme.textMuted }}>
        {note}
        {noteDetail && (
          <div style={{ marginTop: '3px', fontSize: '11px', color: theme.bad }} dir="ltr">
            {noteDetail}
          </div>
        )}
      </div>

      {extraLabel && (
        <button
          type="button"
          disabled={busy}
          onClick={onExtra}
          style={{
            background: 'transparent',
            border: `1px solid ${theme.borderStrong}`,
            borderRadius: '12px',
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: 600,
            color: theme.text,
            cursor: busy ? 'default' : 'pointer',
          }}
        >
          {extraLabel}
        </button>
      )}

      {actionLabel && (
        <button
          type="button"
          disabled={busy}
          onClick={onAction}
          style={{
            background: 'transparent',
            border: `1px solid ${theme.borderStrong}`,
            borderRadius: '12px',
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: 600,
            color: theme.bad,
            cursor: busy ? 'default' : 'pointer',
          }}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

/**
 * A password being chosen, with a way to see it and a way to be given one.
 *
 * Masked by default and revealed on request, which is the opposite of the
 * usual advice for a password box and right here: this is not the reader's
 * own password being typed from memory, it is somebody else's being decided,
 * and it has to be read back before it is read out.
 *
 * autoComplete="new-password" so no browser offers to fill it with the
 * admin's own credentials, and no browser offers to remember a password that
 * belongs to a different person entirely.
 */
function PasswordField({
  id,
  label,
  value,
  shown,
  onValue,
  onShow,
  t,
}: {
  id: string;
  label: string;
  value: string;
  shown: boolean;
  onValue: (value: string) => void;
  onShow: (shown: boolean) => void;
  t: Translate;
}) {
  const weak = value !== '' && !looksStrongEnough(value);

  const button: React.CSSProperties = {
    height: '36px',
    background: 'transparent',
    border: `1px solid ${theme.borderStrong}`,
    borderRadius: theme.radiusControl,
    padding: '0 13px',
    fontSize: '12px',
    fontWeight: 600,
    color: theme.text,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  return (
    <div>
      <label htmlFor={id} style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '5px' }}>
        {label}
      </label>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          id={id}
          type={shown ? 'text' : 'password'}
          dir="ltr"
          autoComplete="new-password"
          value={value}
          onChange={(e) => onValue(e.target.value)}
          style={{
            flex: '1 1 240px',
            minWidth: 0,
            height: '36px',
            border: `1px solid ${weak ? theme.bad : theme.borderStrong}`,
            borderRadius: theme.radiusControl,
            padding: '0 10px',
            fontSize: '13px',
            fontFamily: theme.fontMono,
            background: '#fff',
          }}
        />
        <button type="button" onClick={() => onValue(suggestPassword())} style={button}>
          {t('Suggest one')}
        </button>
        <button type="button" onClick={() => onShow(!shown)} style={button}>
          {t(shown ? 'Hide' : 'Show')}
        </button>
      </div>
      {weak && (
        <div style={{ marginTop: '6px', fontSize: '12px', color: theme.badDeep }}>
          {t('At least 12 characters, with three of: a small letter, a capital, a digit, a symbol.')}
        </div>
      )}
    </div>
  );
}

/**
 * The password that was just set, said once.
 *
 * Deliberately the loudest thing on the screen while it is up, and
 * deliberately dismissed by hand rather than on a timer. It is the only copy
 * in existence, and a panel that vanished while somebody was dialling would
 * be a cruel way to learn that.
 *
 * Beige-gold as a fill with navy on top, which is the pairing the brand
 * allows and measures at 5.9:1. The gold is never the text.
 */
function IssuedPassword({
  who,
  password,
  onDone,
  t,
}: {
  who: string;
  password: string;
  onDone: () => void;
  t: Translate;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <div
      style={{
        marginTop: '14px',
        border: `1px solid ${accent}`,
        borderRadius: theme.radiusCard,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          background: accent,
          color: theme.nav,
          padding: '9px 14px',
          fontSize: '12.5px',
          fontWeight: 700,
        }}
      >
        {t('The password for')} {who}
      </div>

      <div style={{ padding: '14px' }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <code
            dir="ltr"
            style={{
              fontFamily: theme.fontMono,
              fontSize: '19px',
              fontWeight: 600,
              letterSpacing: '0.04em',
              color: theme.text,
              background: theme.surfaceSunken,
              border: `1px solid ${theme.border}`,
              borderRadius: theme.radiusControl,
              padding: '9px 14px',
              userSelect: 'all',
            }}
          >
            {password}
          </code>
          <button
            type="button"
            onClick={() =>
              void navigator.clipboard?.writeText(password).then(
                () => setCopied(true),
                () => setCopied(false),
              )
            }
            style={{
              height: '38px',
              background: ink,
              color: '#fff',
              border: 0,
              borderRadius: theme.radiusControl,
              padding: '0 16px',
              fontSize: '12.5px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {copied ? t('Copied') : t('Copy')}
          </button>
        </div>

        <div style={{ marginTop: '11px', fontSize: '12px', color: theme.textMuted, lineHeight: 1.7 }}>
          {t(
            'Write it down or tell them now. It is shown here once and is not stored anywhere — not by us, and not in a form anybody can read back. If it is lost, set a new one.',
          )}
        </div>

        <button
          type="button"
          onClick={onDone}
          style={{
            marginTop: '11px',
            height: '32px',
            background: 'transparent',
            border: `1px solid ${theme.borderStrong}`,
            borderRadius: theme.radiusControl,
            padding: '0 14px',
            fontSize: '12px',
            fontWeight: 600,
            color: theme.text,
            cursor: 'pointer',
          }}
        >
          {t('I have it')}
        </button>
      </div>
    </div>
  );
}
